import React from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Form, Radio, Table, Tooltip, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  QuestionCircleOutlined
} from '@ant-design/icons';
import useQuotesContext from './QuotesContext';
import CG_MRI from '../../assets/Assurmax-Conditions_Generale_MRI.pdf';

const fractionType = {
  MONTHLY: 1,
  YEARLY: 12
};

const SwitchesDefaultPNO = {
  TYPE1: {
    g1231_fire_related_risks: true,
    g0230_motorized_vehicle_impact: true,
    g1231_natural_disasters: true,
    g1231_technological_risks: true,
    g1231_attacks: true,
    g0230_climatic_events: true,
    g0230_theft_vandalism: true,
    g0030_water_damage: true,
    g0030_broken_glass: true,
    g1231_civil_liability: true,
    g1231_criminal_defense_recourse: true
  }
};

const SwitchesDefault = {
  TYPE1: {
    g1231_property_owner_civil_liability: true,
    g1231_criminal_defense_recourse_following_accident: true
  }
};
SwitchesDefault.TYPE2 = {
  ...SwitchesDefault.TYPE1,
  g1231_natural_disasters: true,
  g0230_motorized_vehicle_impact: true,
  g0230_climatic_events: true,
  g1231_technological_risks: true,
  g1231_fire_related_risks: true,
  g1231_attacks: true
};

SwitchesDefault.TYPE3 = {
  ...SwitchesDefault.TYPE2,
  g0230_theft_vandalism: true,
  g0230_electrical_damage: true,
  g0030_broken_glass: true,
  g0030_water_damage: true,
  g0030_broken_machine: true,
  g0230_collapse: true
};

const ComparisonTableMRI = ({ fields, columns, type, form }) => {
  const { formValues, setFormValues, estimatedCost, exemptions } =
    useQuotesContext();
  const { t } = useTranslation();
  const switchesToUse = type === 'mriml' ? SwitchesDefault : SwitchesDefaultPNO;
  const inputs = {
    switch: (stype, name) => {
      return switchesToUse[stype][name[0]] ? (
        <CheckCircleOutlined />
      ) : (
        <CloseCircleOutlined />
      );
    }
  };

  const handleChangePackage = (guarantee_type) => {
    setFormValues({
      ...formValues,
      guarantee_type,
      TYPE1: {},
      TYPE2: {},
      TYPE3: {},
      [guarantee_type]: switchesToUse[guarantee_type],
      rc_volunteer_union: !!(
        guarantee_type === 'TYPE3' && formValues?.volunteer_trustee
      ),
      ...switchesToUse[guarantee_type],
      ...(guarantee_type !== 'TYPE3' && {
        selected_options: [],
        legal_protection: false,
        extended_legal_protection: false,
        rc_volunteer_union: false
      })
    });

    const allPackageKeys = Object.keys(switchesToUse.TYPE3);
    const packageKeys = Object.keys(switchesToUse[guarantee_type]);

    const packageContentFields = allPackageKeys.map((key) => {
      const value = packageKeys.includes(key);
      return {
        name: [key],
        value
      };
    });

    form.setFields([
      {
        name: 'guarantee_type',
        value: guarantee_type
      },
      {
        name: 'TYPE1',
        value: {}
      },
      {
        name: 'TYPE2',
        value: {}
      },
      {
        name: 'TYPE3',
        value: {}
      },
      {
        name: guarantee_type,
        value: switchesToUse[guarantee_type]
      },
      ...packageContentFields
    ]);

    form.setFieldsValue({
      guarantee_type,
      ...switchesToUse[guarantee_type],
      ...(guarantee_type !== 'TYPE3' && {
        selected_options: [],
        legal_protection_choice: 0
      })
    });
  };

  const costGivenFractionType = (n) =>
    Object.entries(fractionType).map(([key, coef]) => (
      <Typography.Paragraph
        key={key}
        style={{ background: 'none', marginBottom: 0 }}
      >
        {`${(n * coef).toFixed(2)}€/${t(`quotes.fraction_type.${key}`)}`}
      </Typography.Paragraph>
    ));

  const formattedColumns = columns
    .filter((c) => {
      if (!formValues) return c.title;
      if (formValues.empty_space === 'empty_space100') {
        return c.title === 'TYPE1';
      }
      if (
        (formValues.empty_unoccupied_works && type !== 'mriml') ||
        (type === 'mriml' &&
          formValues.empty_unoccupied_works &&
          formValues.empty_space !== 'empty_space025')
      ) {
        return c.title !== 'TYPE3';
      }
      return c.title;
    })
    .map((col) => ({
      ...col,
      title: (
        <Radio
          checked={formValues?.guarantee_type === col.title}
          onChange={() => handleChangePackage(col.title)}
        >
          {type === 'mriml'
            ? t(`contracts.form.options.${col.title}`)
            : 'Formule intégrale'}
        </Radio>
      ),
      align: 'center',
      render: (input, row, index) => {
        if (['estimations1', 'estimations2'].includes(row.name)) {
          if (exemptions.length)
            return `Conditions & tarifs personnalisés sous étude en ligne par notre service (réponse ultra-rapide!)`;
          return costGivenFractionType(input[col.title]);
        }
        if (row.name === 'choices')
          return (
            <Radio
              checked={formValues?.guarantee_type === col.title}
              onChange={() => handleChangePackage(col.title)}
            >
              {type === 'mriml'
                ? t(`contracts.form.options.${col.title}`)
                : 'Formule intégrale'}
            </Radio>
          );
        if (index < fields.length + 1)
          return (
            <Form.Item
              valuePropName={input?.type === 'number' ? undefined : 'checked'}
              name={[col.title, input.name]}
            >
              {inputs[input.type || 'switch'](col.title, input.name)}
            </Form.Item>
          );
        return input;
      }
    }));

  const formatGuaranteeName = (data) => {
    if (data.name[0] === 'g1231_juridic_protection') {
      return (
        <>
          {t(`contracts.form.${data.name}`)}
          <Tooltip
            placement="top"
            title={
              <span>
                {t(`quotes.tooltip.${data.name}1`)}
                <Link
                  to={CG_MRI}
                  target="_blank"
                  style={{ textDecoration: 'underline' }}
                >
                  {t(`quotes.tooltip.CG`)}
                </Link>
                {t(`quotes.tooltip.${data.name}2`)}
              </span>
            }
          >
            <QuestionCircleOutlined style={{ marginLeft: '4px' }} />
          </Tooltip>
        </>
      );
    }
    return t(`contracts.form.${data.name}`);
  };

  const firstColumn = {
    title: 'garanties',
    render: (input) => formatGuaranteeName(input)
  };

  const adjustedFields = [
    { ...estimatedCost, name: 'estimations1' },
    ...fields,
    { ...estimatedCost, name: 'estimations2' },
    { name: 'choices' }
  ];
  return (
    <>
      <Table
        dataSource={adjustedFields}
        columns={[firstColumn, ...formattedColumns]}
        rowKey="name"
        pagination={false}
        scroll={{ y: 550 }}
        className="comparison-table"
      />
      <style>
        {`
      .ant-table .ant-table-tbody > tr > td {
        white-space: break-spaces
      }
    `}
      </style>
    </>
  );
};

ComparisonTableMRI.propTypes = {
  fields: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  type: PropTypes.string.isRequired,
  columns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  form: PropTypes.shape({
    setFieldsValue: PropTypes.func
  }).isRequired
};

export default ComparisonTableMRI;
